// Clingy cart conversion helpers
// Loaded globally so cart-drawer upsell buttons still work after Shopify section/morph refreshes.

const MODULE_FLAG = '__clingyCartConversionLoaded';

if (!window[MODULE_FLAG]) {
  window[MODULE_FLAG] = true;

  const CART_SECTION_SELECTOR = 'cart-items-component[data-section-id]';
  const CART_UPDATE_EVENT = 'cart:update';
  const VARIANT_SCROLL_SELECTOR = '[data-clingy-cart-variant-scroll]';
  const UPSELL_BUTTON_SELECTOR = '[data-clingy-cart-upsell-add]';

  function getCartSectionIds() {
    return Array.from(document.querySelectorAll(CART_SECTION_SELECTOR))
      .map((component) => component.dataset.sectionId)
      .filter(Boolean)
      .filter((id, index, ids) => ids.indexOf(id) === index);
  }

  function dispatchCartUpdate(cart, source, sections) {
    document.dispatchEvent(
      new CustomEvent(CART_UPDATE_EVENT, {
        bubbles: true,
        detail: {
          resource: cart,
          data: {
            source,
            itemCount: cart?.item_count ?? 0,
            sections: sections || {},
          },
        },
      })
    );
  }

  async function readJsonResponse(response) {
    const text = await response.text();
    if (!text) return {};

    try {
      return JSON.parse(text);
    } catch (error) {
      return { status: response.status, message: text };
    }
  }

  async function addVariantToCart(variantId, quantity) {
    const sectionIds = getCartSectionIds();
    const payload = {
      items: [
        {
          id: Number(variantId),
          quantity: Number(quantity) || 1,
        },
      ],
    };

    if (sectionIds.length > 0) {
      payload.sections = sectionIds.join(',');
      payload.sections_url = window.location.pathname;
    }

    const addResponse = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const addData = await readJsonResponse(addResponse);

    if (!addResponse.ok || addData.status >= 400 || addData.description || addData.errors) {
      throw new Error(addData.description || addData.message || addData.errors || 'Could not add this item.');
    }

    const cartResponse = await fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    const cart = await readJsonResponse(cartResponse);

    dispatchCartUpdate(cart, 'clingy-cart-upsell', addData.sections || {});
    return cart;
  }

  let variantScrollDrag = null;

  document.addEventListener(
    'click',
    (event) => {
      const scrollRow = event.target?.closest?.(VARIANT_SCROLL_SELECTOR);
      if (!scrollRow || scrollRow.dataset.clingyScrollDragging !== 'true') return;

      event.preventDefault();
      event.stopImmediatePropagation();
      delete scrollRow.dataset.clingyScrollDragging;
    },
    true
  );

  document.addEventListener('pointerdown', (event) => {
    const scrollRow = event.target?.closest?.(VARIANT_SCROLL_SELECTOR);
    if (!scrollRow) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (scrollRow.scrollWidth <= scrollRow.clientWidth + 4) return;

    variantScrollDrag = {
      row: scrollRow,
      startX: event.clientX,
      scrollLeft: scrollRow.scrollLeft,
      moved: false,
    };
  });

  document.addEventListener(
    'pointermove',
    (event) => {
      if (!variantScrollDrag) return;

      const distance = event.clientX - variantScrollDrag.startX;
      if (Math.abs(distance) < 6) return;

      variantScrollDrag.moved = true;
      variantScrollDrag.row.classList.add('is-dragging');
      variantScrollDrag.row.scrollLeft = variantScrollDrag.scrollLeft - distance;
      event.preventDefault();
    },
    { passive: false }
  );

  function endVariantScrollDrag() {
    if (!variantScrollDrag) return;

    const { row, moved } = variantScrollDrag;
    row.classList.remove('is-dragging');

    if (moved) {
      row.dataset.clingyScrollDragging = 'true';
      window.setTimeout(() => {
        delete row.dataset.clingyScrollDragging;
      }, 150);
    }

    variantScrollDrag = null;
  }

  document.addEventListener('pointerup', endVariantScrollDrag);
  document.addEventListener('pointercancel', endVariantScrollDrag);
  document.addEventListener('lostpointercapture', endVariantScrollDrag);

  document.addEventListener(
    'click',
    async (event) => {
      const button = event.target?.closest?.(UPSELL_BUTTON_SELECTOR);
      if (!button) return;

      event.preventDefault();
      event.stopPropagation();

      const variantId = button.dataset.variantId;
      if (!variantId || button.getAttribute('aria-busy') === 'true') return;

      const originalHTML = button.innerHTML;
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
      button.innerHTML = '<span>Adding…</span>';

      try {
        await addVariantToCart(variantId, button.dataset.quantity || '1');
      } catch (error) {
        console.error('Clingy upsell add failed:', error);
        button.innerHTML = '<span>Try again</span>';
        window.setTimeout(() => {
          if (button.isConnected) button.innerHTML = originalHTML;
        }, 1800);
      } finally {
        if (button.isConnected) {
          button.disabled = false;
          button.removeAttribute('aria-busy');
          if (button.innerHTML.includes('Adding')) {
            button.innerHTML = originalHTML;
          }
        }
      }
    },
    true
  );
}
